import { useState } from 'react'
import { DisplayUserLink, joinDefinedStrings, Modal, tokens } from 'ComponentLibrary'
import { Stack } from '@mui/material'
import {
    ContactForm,
    ContactFormInfoBox,
    type ContactFormCopy,
    DEFAULT_CONTACT_FORM_COPY,
} from 'ComponentLibrary/src/molecules/ContactList/ContactForm'
import { Contact } from 'ComponentLibrary/src/common/users'
import { IconButtonWithOptions } from 'ComponentLibrary/src/piazza'
import { PencilSimpleIcon, TrashIcon, UserCircleIcon } from '@phosphor-icons/react'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type ContactListItemProps = {
    contact: Contact
    updateContact?: (updatedContact: Contact) => void
    deleteContact?: () => void
    copy?: Partial<ContactListItemCopy>
    userLinkCopy?: Partial<UserLinkCopy>
}

export type ContactListItemCopy = {
    edit: string
    delete: string
    editModalTitle: string
    form: ContactFormCopy
}

const DEFAULT_CONTACT_LIST_ITEM_COPY: ContactListItemCopy = {
    edit: 'Bearbeiten',
    delete: 'Löschen',
    editModalTitle: 'Ansprechpartner bearbeiten',
    form: DEFAULT_CONTACT_FORM_COPY,
}

export function ContactListItem({ contact, updateContact, deleteContact, copy, userLinkCopy }: ContactListItemProps) {
    const resolvedCopy = { ...DEFAULT_CONTACT_LIST_ITEM_COPY, ...copy }
    const formCopy = { ...DEFAULT_CONTACT_FORM_COPY, ...copy?.form }
    const [isEditingContact, setIsEditingContact] = useState(false)

    const onSubmitCallback = (data: Contact) => {
        updateContact?.(data)
        setIsEditingContact(false)
    }

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <DisplayUserLink
                user={{ ...contact, organization_name: contact.company, first_name: contact.name }}
                maxWidth="350px"
                defaultIcon={<UserCircleIcon />}
                copy={userLinkCopy}
            />
            {(updateContact || deleteContact) && (
                <IconButtonWithOptions
                    size="small"
                    variant="tertiary"
                    options={[
                        updateContact && {
                            title: resolvedCopy.edit,
                            icon: <PencilSimpleIcon />,
                            callback: () => setIsEditingContact(true),
                        },
                        deleteContact && { title: resolvedCopy.delete, icon: <TrashIcon />, callback: () => deleteContact(), danger: true },
                    ].filter((item) => !!item)}
                />
            )}
            <Modal isOpen={isEditingContact} onClose={() => setIsEditingContact(false)} title={resolvedCopy.editModalTitle} showCloseIcon>
                <Stack direction="column" gap={tokens.SizingSpacing300}>
                    <ContactFormInfoBox text={formCopy.infoBox} />
                    <ContactForm
                        setFormIsOpen={setIsEditingContact}
                        onSubmitCallback={onSubmitCallback}
                        contact={contact}
                        copy={formCopy}
                    />
                </Stack>
            </Modal>
        </Stack>
    )
}

export function getContactTitle(contact: Contact): string {
    const name = joinDefinedStrings([contact.salutation, contact.name])
    return joinDefinedStrings([name, contact.company], ', ')
}
